import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    TextField,
    Button,
    FormControl,
    Select,
    MenuItem,
    Alert,
    CircularProgress,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
} from '@mui/material';
import { ArrowLeft, Lock, Upload } from 'lucide-react';
import {
    createBooking,
    saveBookingDraft,
    generatePaymentLink,
    recordOfflinePayment,
    searchEnquiries,
    searchAffiliates,
    getRetreatsList,
    getRetreatAccommodations,
    getRetreatAddons,
    checkAvailability,
    uploadPaymentProof,
} from '../../Services/Bookings/BookingsApis';

const COUNTRY_CODES = [
    { code: '+91', country: 'India' },
    { code: '+1', country: 'USA' },
    { code: '+44', country: 'UK' },
    { code: '+61', country: 'Australia' },
    { code: '+971', country: 'UAE' },
];

const CHANNEL_OPTIONS = [
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'other', label: 'Other' },
];

const PAYMENT_MODE_OPTIONS = [
    { value: 'deposit_only', label: 'Deposit only' },
    { value: 'full_payment', label: 'Full payment' },
    { value: 'balance_on_arrival', label: 'Balance on arrival' },
];

const OFFLINE_PAYMENT_TYPE_OPTIONS = [
    { value: 'deposit', label: 'Deposit' },
    { value: 'balance', label: 'Balance on arrival' },
    { value: 'full_payment', label: 'Full payment' },
];

const RECEIVED_BY_OPTIONS = [
    { value: 'host_cash', label: 'Host, in cash' },
    { value: 'host_bank', label: 'Host, bank transfer' },
    { value: 'platform_bank', label: 'Platform, bank transfer' },
];

const CreateBooking = () => {
    const navigate = useNavigate();

    // Form state
    const [formData, setFormData] = useState({
        // Booking Source
        channel: 'whatsapp',
        conversationLink: '',
        handledBy: '',
        enquiryId: '',

        // Guest Info
        guestName: '',
        guestEmail: '',
        countryCode: '+91',
        phoneNumber: '',
        country: 'India',

        // Booking Details
        retreatId: '',
        accommodationId: '',
        startDate: '',
        endDate: '',
        guestCount: 1,
        selectedAddons: [],
        priceAdjustment: 0,
        priceAdjustmentReason: '',

        // Cancellation Policy
        cancellationPolicy: '',

        // Affiliate
        affiliateId: '',

        // Payment
        paymentMode: 'deposit_only',
    });

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Data lookup state
    const [retreats, setRetreats] = useState([]);
    const [accommodations, setAccommodations] = useState([]);
    const [addons, setAddons] = useState([]);
    const [availability, setAvailability] = useState(null);
    const [selectedRetreat, setSelectedRetreat] = useState(null);

    // Search results
    const [enquirySearchResults, setEnquirySearchResults] = useState([]);
    const [affiliateSearchResults, setAffiliateSearchResults] = useState([]);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);

    // Offline payment state
    const [offlinePayment, setOfflinePayment] = useState({
        paymentType: 'balance',
        receivedBy: 'host_cash',
        amount: '',
        dateReceived: '',
        proofFile: null,
    });

    // Fetch retreats on mount
    useEffect(() => {
        fetchRetreats();
    }, []);

    // Fetch accommodations when retreat changes
    useEffect(() => {
        if (formData.retreatId) {
            fetchAccommodations(formData.retreatId);
            fetchAddons(formData.retreatId);
            const retreat = retreats.find(r => (r.id || r._id) === formData.retreatId);
            setSelectedRetreat(retreat || null);
        } else {
            setAccommodations([]);
            setAddons([]);
            setSelectedRetreat(null);
        }
    }, [formData.retreatId, retreats]);

    // Check availability when relevant fields change
    useEffect(() => {
        if (formData.retreatId && formData.accommodationId && formData.startDate && formData.endDate) {
            checkBookingAvailability();
        }
    }, [formData.retreatId, formData.accommodationId, formData.startDate, formData.endDate, formData.guestCount]);

    const fetchRetreats = async () => {
        try {
            const response = await getRetreatsList();
            const list = Array.isArray(response?.data)
                ? response.data
                : (Array.isArray(response) ? response : (response?.data?.data || []));
            setRetreats(list);
        } catch (err) {
            console.error('Error fetching retreats:', err);
            setRetreats([]);
        }
    };

    const fetchAccommodations = async (retreatId) => {
        try {
            const response = await getRetreatAccommodations(retreatId);
            const list = Array.isArray(response?.data)
                ? response.data
                : (Array.isArray(response) ? response : (response?.data?.data ? response.data.data : (response?.data ? [response.data] : [])));
            setAccommodations(list);
        } catch (err) {
            console.error('Error fetching accommodations:', err);
            setAccommodations([]);
        }
    };

    const fetchAddons = async (retreatId) => {
        try {
            const response = await getRetreatAddons(retreatId);
            const list = Array.isArray(response?.data)
                ? response.data
                : (Array.isArray(response) ? response : (response?.data?.data || []));
            setAddons(list);
        } catch (err) {
            console.error('Error fetching add-ons:', err);
            setAddons([]);
        }
    };

    const checkBookingAvailability = async () => {
        try {
            const response = await checkAvailability(
                formData.retreatId,
                formData.accommodationId,
                formData.startDate,
                formData.endDate,
                formData.guestCount
            );
            setAvailability(response.data || null);
        } catch (err) {
            console.error('Error checking availability:', err);
            setAvailability(null);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleEnquirySearch = async (searchTerm) => {
        if (searchTerm.length < 2) {
            setEnquirySearchResults([]);
            return;
        }
        try {
            const response = await searchEnquiries(searchTerm);
            setEnquirySearchResults(response.data || []);
        } catch (err) {
            console.error('Error searching enquiries:', err);
        }
    };

    const handleAffiliateSearch = async (searchTerm) => {
        if (searchTerm.length < 2) {
            setAffiliateSearchResults([]);
            return;
        }
        try {
            const response = await searchAffiliates(searchTerm);
            setAffiliateSearchResults(response.data || []);
        } catch (err) {
            console.error('Error searching affiliates:', err);
        }
    };

    const handleSaveDraft = async () => {
        setLoading(true);
        setError(null);
        try {
            await saveBookingDraft(formData);
            setSuccess('Draft saved successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to save draft');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBooking = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await createBooking(formData);
            const bookingId = response.data?.id;

            if (bookingId) {
                await generatePaymentLink(bookingId);
                setSuccess('Booking created and payment link sent');
                setTimeout(() => {
                    navigate('/bookings');
                }, 2000);
            }
        } catch (err) {
            setError(err.message || 'Failed to create booking');
        } finally {
            setLoading(false);
        }
    };

    const handleOfflinePaymentSubmit = async () => {
        setLoading(true);
        setError(null);
        try {
            let proofUrl = '';
            if (offlinePayment.proofFile) {
                const uploadResponse = await uploadPaymentProof(offlinePayment.proofFile);
                proofUrl = uploadResponse.data?.url || '';
            }

            await recordOfflinePayment({
                ...offlinePayment,
                proofUrl,
                bookingId: formData.bookingId,
            });

            setSuccess('Offline payment recorded successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError(err.message || 'Failed to record offline payment');
        } finally {
            setLoading(false);
        }
    };

    // Derived selected accommodation
    const selectedAccommodation = accommodations.find(
        acc => (acc.id || acc._id) === formData.accommodationId
    ) || null;

    const calculateNights = () => {
        if (!formData.startDate || !formData.endDate) return 0;
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        const diffTime = end.getTime() - start.getTime();
        if (diffTime <= 0) return 0;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const calculatePrice = () => {
        const accommodation = selectedAccommodation || accommodations.find(acc => (acc.id || acc._id) === formData.accommodationId);
        if (!accommodation) return 0;
        const basePrice = Number(accommodation.price ?? accommodation.pricePerNight ?? 0);
        const nights = calculateNights();
        const guestCount = parseInt(formData.guestCount, 10) || 1;
        const accommodationTotal = basePrice * nights * guestCount;

        const addonsTotal = (formData.selectedAddons || []).reduce((sum, addonId) => {
            const addon = addons.find(a => (a.id || a._id) === addonId);
            return sum + (Number(addon?.price) || 0);
        }, 0);

        const priceAdj = parseFloat(formData.priceAdjustment) || 0;
        return accommodationTotal + addonsTotal + priceAdj;
    };

    const totalPrice = calculatePrice();
    const depositPercentage = selectedRetreat?.depositPercentage ?? selectedAccommodation?.depositPercentage ?? 20;
    const depositAmount = (totalPrice * depositPercentage) / 100;
    const balanceAmount = totalPrice - depositAmount;

    // Common styles matching standard dashboard forms and BookingDetails.js
    const formSectionSx = {
        mb: 3,
        p: { xs: 2, md: 2.5 },
        border: '1px solid #EAE5D9',
        borderRadius: '8px',
        bgcolor: '#FFFFFF',
        boxShadow: 'none',
    };

    const mainCardSx = {
        mb: 3,
        p: { xs: 2, md: 2.5 },
        border: '1px solid #EAE5D9',
        borderRadius: '8px',
        bgcolor: '#FFFFFF',
        boxShadow: 'none',
    };

    const labelSx = {
        fontSize: '0.825rem',
        fontWeight: 500,
        color: '#334155',
        mb: 0.75,
        display: 'block',
    };

    const inputSx = {
        bgcolor: '#FFFFFF',
        '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            height: '40px',
            fontSize: '0.875rem',
            color: '#1E293B',
            boxShadow: 'none',
            '& fieldset': {
                borderColor: '#E2E8F0',
            },
            '&:hover fieldset': {
                borderColor: '#CBD5E1',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#FFBD65',
                borderWidth: '1px',
            },
            '&.Mui-disabled': {
                bgcolor: '#F8FAFC',
            },
        },
        '& .MuiInputBase-input.Mui-disabled': {
            WebkitTextFillColor: '#64748B',
            color: '#64748B',
        },
    };

    const helperTextSx = {
        fontSize: '0.75rem',
        color: '#64748B',
        mt: 0.5,
        lineHeight: 1.4,
    };

    const cardHeadingSx = {
        fontSize: '1rem',
        fontWeight: 600,
        color: '#2D1810',
        mb: 2,
        lineHeight: 1.4,
    };

    const sectionHeadingSx = {
        fontSize: '0.8rem',
        fontWeight: 600,
        color: '#64748B',
        mb: 1.5,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 2, sm: 2.5, md: 3 }, bgcolor: '#FAF8F5', minHeight: '100vh', boxSizing: 'border-box' }}>
            {/* Top Back Navigation */}
            <Button
                onClick={() => navigate('/bookings')}
                startIcon={<ArrowLeft size={16} />}
                disableElevation
                disableRipple
                sx={{
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 500,
                    color: '#64748B',
                    px: 0,
                    py: 0.5,
                    mb: 1.5,
                    '&:hover': {
                        bgcolor: 'transparent',
                        color: '#1E293B',
                    }
                }}
            >
                Back to Bookings
            </Button>

            {/* Page Title & Subtitle Note */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    sx={{
                        color: '#2D1810',
                        fontWeight: 600,
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        lineHeight: 1.2
                    }}
                >
                    Create Booking
                </Typography>
                <Typography sx={{ color: '#64748B', fontSize: '0.875rem', mt: 0.5, fontWeight: 400 }}>
                    Create a new manual booking and generate a payment link
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '6px', fontSize: '0.85rem' }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2.5, borderRadius: '6px', fontSize: '0.85rem' }}>
                    {success}
                </Alert>
            )}

            <Grid container spacing={2.5}>
                {/* Main Form - Full Width */}
                <Grid size={{ xs: 12 }}>
                    {/* Main Booking Card */}
                    <Box sx={mainCardSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                            <Typography sx={cardHeadingSx}>
                                Booking Information
                            </Typography>
                            <Chip label="Manual" size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 500, borderRadius: '4px' }} />
                        </Box>

                        {/* WHERE DID THIS COME FROM */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={sectionHeadingSx}>
                                WHERE DID THIS COME FROM
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Channel</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={formData.channel}
                                            onChange={(e) => handleInputChange('channel', e.target.value)}
                                            sx={inputSx}
                                        >
                                            {CHANNEL_OPTIONS.map(option => (
                                                <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Link to the chat</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Paste the conversation link..."
                                        value={formData.conversationLink}
                                        onChange={(e) => handleInputChange('conversationLink', e.target.value)}
                                        sx={inputSx}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Handled by</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={formData.handledBy || 'Current User'}
                                        disabled
                                        sx={inputSx}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Existing enquiry?</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search enquiries to attach"
                                        onChange={(e) => handleEnquirySearch(e.target.value)}
                                        sx={inputSx}
                                    />
                                    {enquirySearchResults.length > 0 && (
                                        <Box sx={{ mt: 1, maxHeight: 150, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '6px', bgcolor: '#FFFFFF' }}>
                                            {enquirySearchResults.map(enquiry => (
                                                <Box
                                                    key={enquiry.id}
                                                    sx={{
                                                        p: 1.25,
                                                        borderBottom: '1px solid #F1F5F9',
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: '#F8FAFC' },
                                                    }}
                                                    onClick={() => {
                                                        handleInputChange('enquiryId', enquiry.id);
                                                        setEnquirySearchResults([]);
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                        {enquiry.guest?.name || 'Unknown'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                                                        {enquiry.retreat?.title}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>

                            <Typography sx={{ ...helperTextSx, mt: 1.5 }}>
                                The chat link matters. In three months, when a guest disputes what they were promised, that link is the whole answer.
                            </Typography>
                        </Box>

                        {/* GUEST */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={sectionHeadingSx}>
                                GUEST
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Name</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={formData.guestName}
                                        onChange={(e) => handleInputChange('guestName', e.target.value)}
                                        sx={inputSx}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Email</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={formData.guestEmail}
                                        onChange={(e) => handleInputChange('guestEmail', e.target.value)}
                                        sx={inputSx}
                                    />
                                    <Typography sx={helperTextSx}>
                                        - required, this is where the payment link and invoice go
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Phone</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <FormControl size="small" sx={{ minWidth: 95 }}>
                                            <Select
                                                value={formData.countryCode}
                                                onChange={(e) => handleInputChange('countryCode', e.target.value)}
                                                sx={inputSx}
                                            >
                                                {COUNTRY_CODES.map(option => (
                                                    <MenuItem key={option.code} value={option.code} sx={{ fontSize: '0.85rem' }}>
                                                        {option.code}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={formData.phoneNumber}
                                            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                            sx={inputSx}
                                        />
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Country</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={formData.country}
                                            onChange={(e) => handleInputChange('country', e.target.value)}
                                            sx={inputSx}
                                        >
                                            {COUNTRY_CODES.map(option => (
                                                <MenuItem key={option.country} value={option.country} sx={{ fontSize: '0.85rem' }}>
                                                    {option.country}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                            <Typography sx={{ ...helperTextSx, mt: 1.5 }}>
                                A guest account will be created and the payment link doubles as their invitation to it
                            </Typography>
                        </Box>

                        {/* WHAT THEY ARE BOOKING */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={sectionHeadingSx}>
                                WHAT THEY ARE BOOKING
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Retreat</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={formData.retreatId}
                                            onChange={(e) => handleInputChange('retreatId', e.target.value)}
                                            sx={inputSx}
                                        >
                                            {retreats.map((retreat, index) => {
                                                const rId = retreat.id || retreat._id || `retreat-${index}`;
                                                const rTitle = retreat.title || retreat.name || `Retreat #${index + 1}`;
                                                return (
                                                    <MenuItem key={rId} value={rId} sx={{ fontSize: '0.85rem' }}>
                                                        {rTitle}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Accommodation</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            value={formData.accommodationId}
                                            onChange={(e) => handleInputChange('accommodationId', e.target.value)}
                                            sx={inputSx}
                                            disabled={!formData.retreatId}
                                        >
                                            {accommodations.map((acc, index) => {
                                                const accId = acc.id || acc._id || `acc-${index}`;
                                                const accTitle = acc.title || acc.name || `Accommodation #${index + 1}`;
                                                const price = acc.price ?? acc.pricePerNight;
                                                return (
                                                    <MenuItem key={accId} value={accId} sx={{ fontSize: '0.85rem' }}>
                                                        {accTitle}{price ? ` - ₹${Number(price).toLocaleString()}` : ''}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                    {availability && (
                                        <Box sx={{ mt: 0.75 }}>
                                            <Chip
                                                label={`${availability.spotsAvailable ?? availability.availableSpots ?? availability.openSpots ?? 0} of ${availability.totalSpots ?? availability.defaultOpenSpots ?? 0} spots left`}
                                                size="small"
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    height: 24,
                                                    fontWeight: 500,
                                                    borderRadius: '4px',
                                                    bgcolor: (availability.spotsAvailable ?? availability.availableSpots ?? 0) > 0 ? '#E8F0E5' : '#FEE2E2',
                                                    color: (availability.spotsAvailable ?? availability.availableSpots ?? 0) > 0 ? '#2E7D32' : '#DC2626',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Dates</Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            value={formData.startDate}
                                            onChange={(e) => handleInputChange('startDate', e.target.value)}
                                            sx={inputSx}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            type="date"
                                            value={formData.endDate}
                                            onChange={(e) => handleInputChange('endDate', e.target.value)}
                                            sx={inputSx}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Box>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Guests</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        value={formData.guestCount}
                                        onChange={(e) => handleInputChange('guestCount', parseInt(e.target.value) || 1)}
                                        sx={inputSx}
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Add-ons</Typography>
                                    <FormControl fullWidth size="small">
                                        <Select
                                            multiple
                                            value={formData.selectedAddons}
                                            onChange={(e) => handleInputChange('selectedAddons', e.target.value)}
                                            sx={inputSx}
                                            disabled={!formData.retreatId}
                                        >
                                            {addons.map((addon, index) => {
                                                const addonId = addon.id || addon._id || `addon-${index}`;
                                                const addonName = addon.name || addon.title || `Add-on #${index + 1}`;
                                                const addonPrice = addon.price ? ` - ₹${Number(addon.price).toLocaleString()}` : '';
                                                return (
                                                    <MenuItem key={addonId} value={addonId} sx={{ fontSize: '0.85rem' }}>
                                                        {addonName}{addonPrice}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Price</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={`₹${totalPrice.toLocaleString()}`}
                                        disabled
                                        sx={inputSx}
                                    />
                                    <Typography sx={helperTextSx}>
                                        from the listing, seasonal rate applied
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Typography sx={labelSx}>Adjust price</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            size="small"
                                            type="number"
                                            value={formData.priceAdjustment}
                                            onChange={(e) => handleInputChange('priceAdjustment', parseFloat(e.target.value) || 0)}
                                            sx={{ ...inputSx, width: 160 }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Reason - required for adjustment"
                                            value={formData.priceAdjustmentReason}
                                            onChange={(e) => handleInputChange('priceAdjustmentReason', e.target.value)}
                                            sx={inputSx}
                                        />
                                    </Box>
                                    <Typography sx={helperTextSx}>
                                        Only with a reason — logged
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* CANCELLATION POLICY */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Typography sx={sectionHeadingSx}>
                                    CANCELLATION POLICY
                                </Typography>
                                <Chip label="NEW" size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 500, borderRadius: '4px' }} />
                            </Box>

                            <TableContainer sx={{ mb: 2.5, border: '1px solid #E2E8F0', borderRadius: '6px', bgcolor: '#FFFFFF', boxShadow: 'none' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', textTransform: 'uppercase', borderBottom: '1px solid #E2E8F0' }}>
                                                THE FOUR POLICIES
                                            </TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', borderBottom: '1px solid #E2E8F0' }}>FREE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', borderBottom: '1px solid #E2E8F0' }}>FLEXIBLE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', borderBottom: '1px solid #E2E8F0' }}>MODERATE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', color: '#475569', borderBottom: '1px solid #E2E8F0' }}>CUSTOM</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{ fontSize: '0.825rem', color: '#64748B', borderBottom: '1px solid #F1F5F9' }}>Cancel up to</TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.825rem', color: '#1E293B', borderBottom: '1px solid #F1F5F9' }}>48 hours</TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.825rem', color: '#1E293B', borderBottom: '1px solid #F1F5F9' }}>24 hours</TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.825rem', color: '#1E293B', borderBottom: '1px solid #F1F5F9' }}>7 days</TableCell>
                                            <TableCell align="center" sx={{ fontSize: '0.825rem', color: '#1E293B', borderBottom: '1px solid #F1F5F9' }}>Varies</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography sx={{ fontSize: '0.825rem', fontWeight: 500, color: '#334155', mb: 1 }}>
                                Applies to this booking
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Policy</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={selectedRetreat?.cancellationPolicy || selectedAccommodation?.cancellationPolicy || 'Flexible'}
                                            disabled
                                            sx={inputSx}
                                        />
                                        <Chip
                                            icon={<Lock size={12} />}
                                            label="locked"
                                            size="small"
                                            sx={{ fontSize: '0.75rem', height: 24, bgcolor: '#F1F5F9', color: '#475569', borderRadius: '4px', fontWeight: 500 }}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', mb: 0.3 }}>
                                    Why locked:
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#64748B', lineHeight: 1.5 }}>
                                    It is whatever the host set on the listing. A booking taken on WhatsApp must carry the same terms as the same retreat booked on the website — otherwise two guests on the same dates have different rights, and the one on worse terms is the one who complains.
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', mb: 0.3 }}>
                                    To change it:
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                                    Change it on the listing, not here
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', mb: 0.3 }}>
                                    What the guest gets:
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#64748B', lineHeight: 1.5 }}>
                                    The policy in full on the payment link, the confirmation and the invoice — so it can never be disputed as unclear
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#F8FAFC', borderRadius: '6px', border: '1px solid #E2E8F0' }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#334155', mb: 0.3 }}>
                                    Grace window:
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                                    72 hours from paying, whatever the policy
                                </Typography>
                            </Box>
                        </Box>

                        {/* AFFILIATE */}
                        <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Typography sx={sectionHeadingSx}>
                                    AFFILIATE
                                </Typography>
                                <Chip label="NEW" size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 500, borderRadius: '4px' }} />
                            </Box>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Attach affiliate</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search name or code"
                                        onChange={(e) => handleAffiliateSearch(e.target.value)}
                                        sx={inputSx}
                                    />
                                    {affiliateSearchResults.length > 0 && (
                                        <Box sx={{ mt: 1, maxHeight: 150, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '6px', bgcolor: '#FFFFFF' }}>
                                            {affiliateSearchResults.map(affiliate => (
                                                <Box
                                                    key={affiliate.id}
                                                    sx={{
                                                        p: 1.25,
                                                        borderBottom: '1px solid #F1F5F9',
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: '#F8FAFC' },
                                                    }}
                                                    onClick={() => {
                                                        setSelectedAffiliate(affiliate);
                                                        handleInputChange('affiliateId', affiliate.id);
                                                        setAffiliateSearchResults([]);
                                                    }}
                                                >
                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1E293B' }}>
                                                        {affiliate.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
                                                        Code: {affiliate.code}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Grid>

                                {selectedAffiliate && (
                                    <>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography sx={labelSx}>Attached</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={`${selectedAffiliate.name} - code ${selectedAffiliate.code}`}
                                                disabled
                                                sx={inputSx}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography sx={labelSx}>Their tier</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={`${selectedAffiliate.commissionRate}% of our commission — ${selectedAffiliate.bookingsThisMonth} bookings this month`}
                                                disabled
                                                sx={inputSx}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography sx={labelSx}>They will earn</Typography>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={`₹${((totalPrice * 0.2) * (selectedAffiliate.commissionRate / 100)).toLocaleString()} (${selectedAffiliate.commissionRate}% of our ₹${(totalPrice * 0.2).toLocaleString()} commission)`}
                                                disabled
                                                sx={inputSx}
                                            />
                                        </Grid>

                                        <Grid size={{ xs: 12 }}>
                                            <Typography sx={labelSx}>Status on creation</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip label="pending" size="small" sx={{ fontSize: '0.75rem', height: 24, bgcolor: '#FEF3C7', color: '#92400E', borderRadius: '4px', fontWeight: 500 }} />
                                                <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                                                    — confirms when the guest completes the retreat, per the affiliate spec
                                                </Typography>
                                            </Box>
                                        </Grid>

                                        <Grid size={{ xs: 12 }}>
                                            <Typography sx={helperTextSx}>
                                                Cookie not involved: A manual booking has no click to trace, so the affiliate is attached by hand and marked as such
                                            </Typography>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>

                        {/* MONEY */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={sectionHeadingSx}>
                                PAYMENT & PRICING
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Payment mode</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={formData.paymentMode}
                                                onChange={(e) => handleInputChange('paymentMode', e.target.value)}
                                                sx={inputSx}
                                            >
                                                {PAYMENT_MODE_OPTIONS.map(option => (
                                                    <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#64748B', mt: 0.5 }}>
                                        — as set on this retreat
                                    </Typography>
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Deposit now</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={`₹${depositAmount.toLocaleString()} (${depositPercentage}% slab + GST)`}
                                        disabled
                                        sx={inputSx}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Balance</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={`₹${balanceAmount.toLocaleString()} due on arrival`}
                                        disabled
                                        sx={inputSx}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography sx={labelSx}>Held in escrow</Typography>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value="Yes — released after the host confirms arrival"
                                        disabled
                                        sx={inputSx}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid #EAE5D9' }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleSaveDraft}
                                    disabled={loading}
                                    sx={{
                                        borderColor: '#CBD5E1',
                                        color: '#334155',
                                        textTransform: 'none',
                                        borderRadius: '6px',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        boxShadow: 'none',
                                        height: '40px',
                                        px: 2.5,
                                        '&:hover': {
                                            borderColor: '#94A3B8',
                                            bgcolor: '#F8FAFC',
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    Save as draft
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleCreateBooking}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null}
                                    sx={{
                                        bgcolor: '#3D1F10',
                                        color: '#FFFFFF',
                                        textTransform: 'none',
                                        borderRadius: '6px',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        boxShadow: 'none',
                                        height: '40px',
                                        px: 2.5,
                                        '&:hover': {
                                            bgcolor: '#2A150B',
                                            boxShadow: 'none',
                                        }
                                    }}
                                >
                                    Create and send payment link
                                </Button>
                            </Box>
                            <Typography sx={{ ...helperTextSx, mt: 1.5 }}>
                                The guest receives the link by email and WhatsApp. The spot is held for 48 hours; if they do not pay, it is released and the booking lapses on its own.
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                {/* Recording a payment taken outside the platform - Separate Card */}
                <Grid size={{ xs: 12 }}>
                    <Box sx={formSectionSx}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography sx={cardHeadingSx}>
                                Recording a payment taken outside the platform
                            </Typography>
                            <Chip label="NEW" size="small" sx={{ fontSize: '0.7rem', height: 22, bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 500, borderRadius: '4px' }} />
                        </Box>

                        <Alert
                            severity="error"
                            sx={{
                                mb: 2.5,
                                borderRadius: '6px',
                                fontSize: '0.825rem',
                                bgcolor: '#FEF2F2',
                                color: '#991B1B',
                                border: '1px solid #FECACA',
                                '& .MuiAlert-icon': {
                                    color: '#DC2626',
                                },
                            }}
                        >
                            Last resort, not a shortcut. Use this only when money has genuinely already been received — a bank transfer to us, or cash the host took under a balance-on-arrival policy. It cannot be used for the deposit unless the money actually reached our account.
                        </Alert>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>What was paid</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={offlinePayment.paymentType}
                                        onChange={(e) => setOfflinePayment(prev => ({ ...prev, paymentType: e.target.value }))}
                                        sx={inputSx}
                                    >
                                        {OFFLINE_PAYMENT_TYPE_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>Received by</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={offlinePayment.receivedBy}
                                        onChange={(e) => setOfflinePayment(prev => ({ ...prev, receivedBy: e.target.value }))}
                                        sx={inputSx}
                                    >
                                        {RECEIVED_BY_OPTIONS.map(option => (
                                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.85rem' }}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>Amount</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    value={offlinePayment.amount}
                                    onChange={(e) => setOfflinePayment(prev => ({ ...prev, amount: e.target.value }))}
                                    sx={inputSx}
                                    InputProps={{
                                        startAdornment: <Typography sx={{ mr: 1, color: '#64748B', fontSize: '0.875rem' }}>₹</Typography>,
                                    }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>Date received</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    value={offlinePayment.dateReceived}
                                    onChange={(e) => setOfflinePayment(prev => ({ ...prev, dateReceived: e.target.value }))}
                                    sx={inputSx}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>Proof</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        component="label"
                                        startIcon={<Upload size={16} />}
                                        sx={{
                                            borderColor: '#CBD5E1',
                                            color: '#334155',
                                            textTransform: 'none',
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            fontWeight: 500,
                                            height: '40px',
                                            '&:hover': {
                                                borderColor: '#94A3B8',
                                                bgcolor: '#F8FAFC',
                                            }
                                        }}
                                    >
                                        Upload receipt or screenshot
                                        <input
                                            type="file"
                                            hidden
                                            accept="image/*,.pdf"
                                            onChange={(e) => setOfflinePayment(prev => ({ ...prev, proofFile: e.target.files[0] }))}
                                        />
                                    </Button>
                                    {offlinePayment.proofFile && (
                                        <Typography sx={{ fontSize: '0.8rem', color: '#2E7D32', fontWeight: 500 }}>
                                            {offlinePayment.proofFile.name}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography sx={helperTextSx}>
                                    — required, no proof means no entry
                                </Typography>
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>Who can do this</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value="Finance - Master Admin"
                                    disabled
                                    sx={inputSx}
                                />
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography sx={labelSx}>Recorded as</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value="Offline payment, with your name against it, in the log book"
                                    disabled
                                    sx={inputSx}
                                />
                            </Grid>
                        </Grid>

                        <Typography sx={{ fontSize: '0.8rem', color: '#64748B', mt: 2, lineHeight: 1.5 }}>
                            Money the host collected in cash never enters escrow and is not ours to refund. If that booking is later cancelled, the refund of that portion is the host's to make — and the panel adds it to what the host owes, to come off their next payout.
                        </Typography>

                        <Box sx={{ mt: 2.5 }}>
                            <Button
                                variant="outlined"
                                onClick={handleOfflinePaymentSubmit}
                                disabled={loading || !offlinePayment.proofFile}
                                sx={{
                                    borderColor: '#DC2626',
                                    color: '#DC2626',
                                    textTransform: 'none',
                                    borderRadius: '6px',
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                    height: '40px',
                                    px: 2.5,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        borderColor: '#B91C1C',
                                        bgcolor: '#FEF2F2',
                                        boxShadow: 'none',
                                    }
                                }}
                            >
                                Record offline payment
                            </Button>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CreateBooking;
